import { ButtonProps, useModal } from "@homebound/beam";
import { ReadyPlanStatus } from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import {
  PromoteToCheckoutStateDocument,
  usePromoteToCheckout_PromoteMutation,
  usePromoteToCheckout_RestoreMutation,
  usePromoteToCheckoutStateQuery,
} from "src/routes/developments/product-offerings/components/usePromoteToCheckout.generated";
import { noop } from "src/utils";

/**
 * Promote `versionId` to Checkout, or restore it when it was previously promoted.
 * Usable anywhere that needs to handle promote to checkout so we have the same conditions and mutations.
 */
export function usePromoteToCheckout(
  productOfferingId: string,
  versionId: string,
): { isRestore: boolean; disabled: ButtonProps["disabled"]; onClick: () => void } {
  const { data } = usePromoteToCheckoutStateQuery({ variables: { id: productOfferingId, versionId } });
  const { openModal } = useModal();
  const productOffering = data?.productOffering;
  const version = data?.readyPlanAggregateVersion;
  if (!productOffering || !version) return { isRestore: false, disabled: true, onClick: noop };

  const isArchived = productOffering.status.code === ReadyPlanStatus.Archived;
  const isCheckoutVersion = version.id === productOffering.aggregateCheckout?.id;
  const hasBeenPromoted = version.promotedAt != null;
  const canPromote = version.canPromote.allowed;
  const canRestore = hasBeenPromoted && !isCheckoutVersion && !isArchived;
  const modalProps = { productOfferingId, aggregateVersionId: versionId };

  return {
    isRestore: hasBeenPromoted && !isCheckoutVersion,
    disabled:
      canPromote || canRestore ? false : version.canPromote.disabledReasons.map((r) => r.message).join(", ") || true,
    onClick: () =>
      openModal({
        content: canPromote ? <PromoteToCheckoutModal {...modalProps} /> : <RestoreToCheckoutModal {...modalProps} />,
      }),
  };
}

type CheckoutModalProps = { productOfferingId: string; aggregateVersionId: string };

function PromoteToCheckoutModal(props: CheckoutModalProps) {
  const { productOfferingId, aggregateVersionId } = props;
  const [promoteToCheckout] = usePromoteToCheckout_PromoteMutation();
  const { closeModal } = useModal();
  return (
    <ConfirmationModal
      title="Promote to Checkout"
      label="Promote"
      onConfirmAction={async () => {
        await promoteToCheckout({
          variables: { input: { aggregateVersionId } },
          refetchQueries: [checkoutStateRefetch(productOfferingId, aggregateVersionId)],
          awaitRefetchQueries: true,
        });
        closeModal();
      }}
      confirmationMessage="This will set the current version as the Checkout Version."
    />
  );
}

function RestoreToCheckoutModal(props: CheckoutModalProps) {
  const { productOfferingId, aggregateVersionId } = props;
  const [restoreToCheckout] = usePromoteToCheckout_RestoreMutation();
  const { closeModal } = useModal();
  return (
    <ConfirmationModal
      title="Restore to Checkout"
      label="Restore"
      onConfirmAction={async () => {
        await restoreToCheckout({
          variables: { input: { id: productOfferingId, aggregateCheckoutId: aggregateVersionId } },
          refetchQueries: [checkoutStateRefetch(productOfferingId, aggregateVersionId)],
          awaitRefetchQueries: true,
        });
        closeModal();
      }}
      confirmationMessage="This will restore the previously promoted version as the Checkout Version."
    />
  );
}

// Refetch only the checkout state, which Apollo merges into the cached offering and version
function checkoutStateRefetch(id: string, versionId: string) {
  return { query: PromoteToCheckoutStateDocument, variables: { id, versionId } };
}
